// ─────────────────────────────────────────────────────────────────
// Glass-i — Glass Type Master page (Stage 6B.8A-3M).
//
// COMPACT INLINE CRUD — no drawer, no modal. Every cell edits live;
// dirty rows highlight in amber; Save All Changes commits via the
// /bulk-save endpoint in one round-trip.
//
// Strictly independent — not linked to Process Master.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Input, InputNumber, Space, Spin, Table, Tooltip, message
} from 'antd';
import {
  PlusOutlined, SaveOutlined, ReloadOutlined,
  AppstoreOutlined, SearchOutlined
} from '@ant-design/icons';

import { glassTypeApi } from './glassTypeApi';
import {
  normalizeGlassTypeRows, sortGlassTypes, nextSno,
  validateGlassTypeRows, buildGlassTypeBulkPayload, markDirtyRow
} from './glassTypeHelpers';
import './GlassTypeMasterPage.css';

export default function GlassTypeMasterPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await glassTypeApi.list({ limit: 1000 });
      setRows(sortGlassTypes(normalizeGlassTypeRows((out && out.rows) || [])));
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to load glass types.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── Filtering ────────────────────────────────────────────────
  const visible = useMemo(() => {
    const needle = (search || '').trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.glassType, r.remarks, String(r.sno)]
        .some((v) => v && String(v).toLowerCase().includes(needle)));
  }, [rows, search]);

  const dirtyCount = useMemo(
    () => rows.filter((r) => r._dirty).length, [rows]);

  // ── Row mutations (in-memory only — saved on demand) ─────────
  const patch = (clientRowId, p) =>
    setRows((prev) => markDirtyRow(prev, clientRowId, p));

  const addRow = () => {
    setRows((prev) => {
      const fresh = {
        _clientRowId: `gt-new-${Date.now()}-${prev.length + 1}`,
        _dirty: true,
        id:        null,
        sno:       nextSno(prev),
        glassType: '',
        isActive:  true,   // defensive default — Active column removed from UI
        remarks:   ''
      };
      return [...prev, fresh];
    });
  };

  // ── Save All ─────────────────────────────────────────────────
  const saveAll = async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (dirty.length === 0) {
      message.info('Nothing to save.');
      return;
    }
    const { ok, errors } = validateGlassTypeRows(rows);
    if (!ok) {
      message.error(errors[0].message);
      return;
    }
    setSaving(true);
    try {
      const payload = buildGlassTypeBulkPayload(rows, []);
      const out = await glassTypeApi.bulkSave(payload);
      message.success(
        `Saved ${out.inserted || 0} new · updated ${out.updated || 0}.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || 'Failed to save glass types.');
    } finally {
      setSaving(false);
    }
  };

  // ── Columns (Active + Actions removed per stage 6B.8A-3M.1) ──
  const columns = [
    { title: 'S No', dataIndex: 'sno', width: 84, align: 'right',
      render: (v, row) => (
        <InputNumber size="small" min={1} step={1}
          value={v}
          variant="borderless" controls={false}
          style={{ width: '100%', textAlign: 'right' }}
          onChange={(n) => patch(row._clientRowId, {
            sno: n == null ? null : Math.trunc(Number(n)) })} />
      ) },
    { title: 'Glass Type', dataIndex: 'glassType', width: 280,
      render: (v, row) => (
        <Input size="small" variant="borderless"
          value={v} placeholder="e.g., A-BLOCK"
          maxLength={80}
          onChange={(e) => patch(row._clientRowId,
            { glassType: e.target.value })} />
      ) },
    { title: 'Remarks', dataIndex: 'remarks',
      render: (v, row) => (
        <Input size="small" variant="borderless"
          value={v} placeholder="Optional notes"
          maxLength={2000}
          onChange={(e) => patch(row._clientRowId,
            { remarks: e.target.value })} />
      ) }
  ];

  return (
    <div className="gt-shell">
      {/* Header */}
      <div className="gt-headstrip">
        <div className="gt-headstrip__lead">
          <span className="gt-headstrip__icon"><AppstoreOutlined /></span>
          <div className="gt-headstrip__text">
            <span className="gt-headstrip__title">Glass Type Master</span>
            <span className="gt-headstrip__subtitle">
              Edit any cell directly — click Save All Changes to persist.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="gt-stat-chip"><strong>{rows.length}</strong> total</span>
          {dirtyCount > 0 && (
            <span className="gt-stat-chip gt-stat-chip--dirty">
              <strong>{dirtyCount}</strong> unsaved
            </span>
          )}
        </Space>
      </div>

      {/* Toolbar */}
      <div className="gt-toolbar">
        <Input allowClear size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search glass type, remarks…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }} />
        <div className="gt-toolbar__spacer" />
        <Tooltip title="Refresh"><Button size="small"
          icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        <Button size="small" icon={<PlusOutlined />} onClick={addRow}>
          Add Row
        </Button>
        <Button size="small" type="primary" icon={<SaveOutlined />}
          onClick={saveAll} loading={saving}
          disabled={saving || dirtyCount === 0}>
          Save All Changes
        </Button>
      </div>

      {/* Grid */}
      <div className="gt-card">
        <Spin spinning={loading}>
          <Table className="gt-grid" size="small"
            rowKey="_clientRowId"
            dataSource={visible}
            columns={columns}
            pagination={false}
            sticky
            scroll={{ x: 'max-content' }}
            rowClassName={(r) => r._dirty ? 'gt-dirty-row' : ''}
            locale={{
              emptyText: rows.length === 0
                ? 'No glass types yet. Click Add Row to start.'
                : 'No glass types match the current search.'
            }} />
        </Spin>
      </div>
    </div>
  );
}
